'use client';

import { useMemo, useState } from 'react';
import { ActivityIcon, ArrowCounterClockwiseIcon, PencilIcon, SparkleIcon } from '@phosphor-icons/react';
import { useRouter } from '@/lib/router';
import { useFinance } from '@/hooks/useFinance';
import { track } from '@/lib/telemetry';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { token } from '@/lib/theme/tokens';
import { useThemeColors } from '@/lib/theme/useThemeColors';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import type { GoalSummary } from '@/types';
import {
  addMonthsToToday,
  formatMonthYear,
  formatYmShort,
  getGoalIcon,
  projectedMonthsToTarget,
} from './helpers';
import { Slider } from './Slider';
import { TrajectoryChart } from './TrajectoryChart';

const HISTORY_RANGE_LABEL = 'Últimos 6 meses · projeção da meta';

function LegendDot({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden
        className="inline-block h-[2px] w-3.5 rounded-full"
        style={{
          backgroundColor: dashed ? 'transparent' : color,
          backgroundImage: dashed
            ? `linear-gradient(to right, ${color} 0 4px, transparent 4px 7px, ${color} 7px 11px, transparent 11px 14px)`
            : undefined,
        }}
      />
      {label}
    </span>
  );
}

interface Props {
  goal: GoalSummary | null;
  onClose: () => void;
  onApplied?: () => void;
}

export function GoalSimulator({ goal, onClose, onApplied }: Props) {
  const router = useRouter();
  const { updateGoal } = useFinance();
  const formatCurrency = useFormatCurrency();
  const theme = useThemeColors();

  const isDebt = goal?.type === 'DebtPayoff';
  const isReserve = goal?.type === 'Reserve';

  const [simContribution, setSimContribution] = useState(goal?.monthlyContribution ?? 0);
  const [simTarget, setSimTarget] = useState(goal?.targetAmount ?? 0);
  const [simDeadline, setSimDeadline] = useState(goal?.targetDate ? goal.targetDate.slice(0, 7) : '');
  const [saving, setSaving] = useState(false);

  // reset state on goal change
  useMemo(() => {
    if (goal) {
      setSimContribution(goal.monthlyContribution ?? 0);
      setSimTarget(goal.targetAmount);
      setSimDeadline(goal.targetDate ? goal.targetDate.slice(0, 7) : '');
    }
  }, [goal]);

  if (!goal) return null;

  const Icon = getGoalIcon(goal.icon, goal.type);
  const originalContribution = goal.monthlyContribution ?? 0;
  const originalTarget = goal.targetAmount;
  const originalDeadline = goal.targetDate ? goal.targetDate.slice(0, 7) : '';

  const baseMonths = projectedMonthsToTarget(goal);
  const simMonths = (() => {
    if (simContribution <= 0) return Infinity;
    const remaining = (isDebt ? goal.currentBalance ?? simTarget : simTarget) - (isDebt ? 0 : goal.currentAmount);
    if (remaining <= 0) return 0;
    return Math.ceil(remaining / simContribution);
  })();

  const baseCompletion = baseMonths === Infinity ? null : addMonthsToToday(baseMonths);
  const simCompletion = simMonths === Infinity ? null : addMonthsToToday(simMonths);
  const monthsDiff =
    baseMonths === Infinity || simMonths === Infinity ? null : simMonths - baseMonths;

  const baseTotal = originalContribution * (baseMonths === Infinity ? 0 : baseMonths);
  const simTotal = simContribution * (simMonths === Infinity ? 0 : simMonths);

  const minContrib = 0;
  const maxContrib = Math.max(1, Math.round((originalContribution || 100000 / 100) * 3));

  const hasChanges =
    simContribution !== originalContribution ||
    simTarget !== originalTarget ||
    simDeadline !== originalDeadline;

  const reset = () => {
    setSimContribution(originalContribution);
    setSimTarget(originalTarget);
    setSimDeadline(originalDeadline);
  };

  const applySimulation = async () => {
    if (!hasChanges) return;
    setSaving(true);
    try {
      await updateGoal(goal.id, {
        monthlyContribution: simContribution || undefined,
        clearMonthlyContribution: !simContribution,
        targetAmount: simTarget,
        targetDate: simDeadline ? `${simDeadline}-01` : undefined,
        clearTargetDate: !simDeadline,
      });
      track('action.goal.update', { via: 'simulator' });
      onApplied?.();
      onClose();
    } catch {
      // handled
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={!!goal} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full gap-0 border-l border-border-subtle bg-surface p-0 shadow-lg sm:max-w-[640px]"
      >
        <SheetHeader className="border-b border-border-subtle bg-surface-sunken/50 px-5 py-4 md:px-6">
          <div className="flex min-w-0 items-center gap-3 pr-8">
            <div
              className="grid size-9 shrink-0 place-items-center rounded-md"
              style={{
                backgroundColor: isDebt ? 'rgba(225,29,72,0.10)' : 'rgba(5,150,105,0.12)',
                color: isDebt ? token('--error', '#e11d48') : token('--success', '#059669'),
              }}
            >
              <Icon size={16} weight="bold" />
            </div>
            <div className="min-w-0">
              <SheetDescription className="mb-0.5 text-2xs text-text-secondary">Simular ajuste</SheetDescription>
              <SheetTitle className="truncate text-base leading-tight font-semibold text-text-primary">
                {goal.name}
              </SheetTitle>
            </div>
          </div>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-5 md:px-6">
          <div className="contents sm:order-3 sm:flex sm:flex-col sm:gap-4">
            <div className="order-1">
              <Slider
                label={isDebt ? 'Pagamento mensal' : 'Contribuição mensal'}
                value={simContribution}
                originalValue={originalContribution}
                min={minContrib}
                max={maxContrib}
                step={50}
                onChange={setSimContribution}
                formatValue={formatCurrency}
              />
            </div>

            <div className="order-4 space-y-4">
              {!isDebt && !isReserve && (
                <Slider
                  label="Valor da meta"
                  value={simTarget}
                  originalValue={originalTarget}
                  min={Math.max(1, Math.round(originalTarget * 0.5))}
                  max={Math.max(2, Math.round(originalTarget * 2))}
                  step={100}
                  onChange={setSimTarget}
                  formatValue={formatCurrency}
                />
              )}

              {!isReserve && (
                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label className="text-xs font-medium text-text-primary">Prazo</label>
                    <span className="font-mono text-2xs text-text-secondary">
                      atual: {originalDeadline ? formatYmShort(originalDeadline) : 'sem prazo'}
                    </span>
                  </div>
                  <input
                    type="month"
                    value={simDeadline}
                    onChange={(e) => setSimDeadline(e.target.value)}
                    className="w-full rounded-md border border-border-subtle bg-surface px-3 py-1.5 font-mono text-xs focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="order-2 rounded-xl border border-border-subtle bg-surface p-4 shadow-sm sm:order-1">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-700">
                  <ActivityIcon size={14} weight="duotone" />
                </span>
                <div className="min-w-0">
                  <p className="text-2xs leading-tight font-semibold tracking-widest text-text-secondary uppercase">Trajetória de saldo</p>
                  <p className="mt-0.5 text-2xs leading-tight text-text-tertiary">{HISTORY_RANGE_LABEL}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-2xs text-text-secondary">
                <LegendDot color={theme.primary} label="histórico" />
                {hasChanges ? (
                  <>
                    <LegendDot color={token('--amount-neutral', '#9AA29E')} label="atual" dashed />
                    <LegendDot color={token('--success', '#167A60')} label="simulação" />
                  </>
                ) : (
                  <LegendDot color={token('--amount-neutral', '#9AA29E')} label="projeção" />
                )}
              </div>
            </div>
            <TrajectoryChart
              goal={goal}
              simulatedContribution={simContribution}
              simulatedTarget={isDebt ? null : simTarget}
              simulatedDeadline={simDeadline ? `${simDeadline}-01` : null}
              height={240}
            />
          </div>

          <div className="order-3 grid grid-cols-3 gap-2 rounded-xl border border-border-subtle bg-surface p-3 shadow-none sm:order-2 sm:gap-3">
            <ComparisonCell
              label="Conclusão"
              base={baseCompletion ? formatMonthYear(baseCompletion) : '—'}
              sim={simCompletion ? formatMonthYear(simCompletion) : 'nunca'}
              delta={
                monthsDiff === null
                  ? null
                  : monthsDiff === 0
                    ? 'igual'
                    : `${monthsDiff > 0 ? '+' : ''}${monthsDiff} ${Math.abs(monthsDiff) === 1 ? 'mês' : 'meses'}`
              }
              deltaIsGood={monthsDiff !== null && monthsDiff < 0}
            />
            <ComparisonCell
              label={isDebt ? 'Total pago' : 'Total aportado'}
              base={formatCurrency(baseTotal)}
              sim={formatCurrency(simTotal)}
              delta={`${simTotal > baseTotal ? '+' : ''}${formatCurrency(simTotal - baseTotal)}`}
              deltaIsGood={isDebt ? simTotal < baseTotal : false}
              deltaNeutral={!isDebt}
            />
            <ComparisonCell
              label="Tempo total"
              base={baseMonths === Infinity ? '—' : `${baseMonths} m`}
              sim={simMonths === Infinity ? 'nunca' : `${simMonths} m`}
              delta={
                monthsDiff === null
                  ? null
                  : monthsDiff === 0
                    ? '—'
                    : `${monthsDiff > 0 ? '+' : ''}${monthsDiff} m`
              }
              deltaIsGood={monthsDiff !== null && monthsDiff < 0}
            />
          </div>

          <div className="order-5 flex items-start gap-2 rounded-md border border-border-subtle bg-surface-sunken p-3 text-2xs leading-relaxed text-text-secondary">
            <SparkleIcon size={12} className="mt-0.5 shrink-0 text-success-text" />
            <span>
              {isDebt
                ? 'Aumentar o pagamento mensal reduz juros pagos no total e antecipa a quitação.'
                : 'Aumentar a contribuição ou esticar o prazo são as duas alavancas principais. Pequenos aportes consistentes superam aportes esporádicos.'}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border-subtle bg-surface-sunken/50 px-5 py-3 md:px-6">
          <button
            onClick={reset}
            disabled={!hasChanges}
            className="flex items-center gap-1.5 text-xs text-text-secondary hover:underline disabled:opacity-30"
          >
            <ArrowCounterClockwiseIcon size={12} />
            Resetar
          </button>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              onClick={() => router.push(`/metas/${goal.id}/edit`)}
              className="h-8 px-3 text-xs"
            >
              <PencilIcon size={12} className="mr-1" />
              Editar
            </Button>
            <Button
              onClick={applySimulation}
              disabled={!hasChanges || saving}
              className="h-8 bg-primary px-3 text-xs text-primary-foreground hover:bg-primary-hover"
            >
              {saving ? 'Salvando…' : 'Aplicar simulação'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ComparisonCell({
  label,
  base,
  sim,
  delta,
  deltaIsGood,
  deltaNeutral,
}: {
  label: string;
  base: string;
  sim: string;
  delta: string | null;
  deltaIsGood?: boolean;
  deltaNeutral?: boolean;
}) {
  const deltaColor = deltaNeutral
    ? 'text-text-secondary'
    : deltaIsGood
      ? 'text-success-text'
      : delta && delta !== 'igual' && delta !== '—'
        ? 'text-error'
        : 'text-text-secondary';
  return (
    <div>
      <div className="mb-1 text-2xs tracking-wide text-text-secondary uppercase">{label}</div>
      <div className="truncate font-mono text-sm font-medium text-text-primary tabular-nums">{sim}</div>
      <div className="mt-0.5 truncate font-mono text-2xs text-text-tertiary tabular-nums">
        de <span className="line-through">{base}</span>
      </div>
      {delta && <div className={`text-2xs font-medium mt-1 ${deltaColor} font-mono`}>{delta}</div>}
    </div>
  );
}
